import { Handbook } from '~/types/Handbook';
import { KsaBankAccount, KsaReviewBankAccount } from '~/types/ksa/KsaBankAccount';

export const ENGLISH_BANK_NAME = /^[A-Za-z]+(?: +[A-Za-z]+)*$/;
/** Kept for legacy callers that still rely on a quick regex check (e.g. findBank). */
export const SAUDI_IBAN = /^SA\d{2}[A-Z0-9]{20}$/;

/**
 * Full Saudi IBAN validation.
 * Returns null on success, or a key describing the specific error.
 *
 * Rules:
 *  1. Exactly 24 characters after stripping spaces.
 *  2. Starts with "SA".
 *  3. Characters 3-4 are digits (ISO check digits).
 *  4. Characters 5-24 are alphanumeric (BBAN).
 *  5. Passes the standard MOD-97 checksum.
 */
export type IbanErrorKey =
  | 'IBAN invalid length'
  | 'IBAN must start with SA'
  | 'IBAN check digits invalid'
  | 'IBAN body must be alphanumeric'
  | 'IBAN checksum invalid';

export const validateSaudiIban = (raw: string): IbanErrorKey | null => {
  const iban = raw.replace(/\s/g, '').toUpperCase();

  if (iban.length !== 24) return 'IBAN invalid length';
  if (!iban.startsWith('SA')) return 'IBAN must start with SA';
  if (!/^\d{2}$/.test(iban.slice(2, 4))) return 'IBAN check digits invalid';
  if (!/^[A-Z0-9]{20}$/.test(iban.slice(4))) return 'IBAN body must be alphanumeric';

  // MOD-97 checksum: move first 4 chars to end, convert letters to digits (A=10…Z=35)
  const rearranged = iban.slice(4) + iban.slice(0, 4);
  const numeric = rearranged
    .split('')
    .map((ch) => (ch >= 'A' && ch <= 'Z' ? String(ch.charCodeAt(0) - 55) : ch))
    .join('');

  // BigInt mod-97 (handles arbitrary length strings)
  let remainder = 0;
  for (const digit of numeric) {
    remainder = (remainder * 10 + parseInt(digit, 10)) % 97;
  }

  if (remainder !== 1) return 'IBAN checksum invalid';
  return null;
};
export const SWIFT_CODE = /^[A-Z]{4}[A-Z]{2}[A-Z0-9]{2}([A-Z0-9]{3})?$/;

export const normalizeIban = (value: string) => value.replace(/\s/g, '').toUpperCase();
export const normalizeSwiftCode = (value: string) => value.trim().toUpperCase();

export const normalizeBankReview = (review: KsaReviewBankAccount): KsaReviewBankAccount => {
  const result = { ...review };
  if (typeof result.englishBeneficiaryFullName === 'string') {
    result.englishBeneficiaryFullName = result.englishBeneficiaryFullName.trim();
  }
  if (typeof result.agentBankName === 'string') result.agentBankName = result.agentBankName.trim();
  if (typeof result.derivedBankName === 'string') result.derivedBankName = result.derivedBankName.trim();
  if (typeof result.agentIban === 'string') result.agentIban = normalizeIban(result.agentIban);
  if (typeof result.agentSwiftCode === 'string') {
    result.agentSwiftCode = normalizeSwiftCode(result.agentSwiftCode);
  }
  if (result.agentSwiftCode === undefined) delete result.agentSwiftCode;
  return result;
};

export const isBankVerified = (bankAccount?: KsaBankAccount, iban = bankAccount?.iban) =>
  bankAccount?.verified === true &&
  bankAccount.bankIntegrationRes?.showUploadField !== true &&
  bankAccount.bankIntegrationRes?.retryable !== true &&
  bankAccount.bankIntegrationRes?.stopRetry !== false &&
  normalizeIban(iban || '') === normalizeIban(bankAccount.iban || '');

export const findBank = (iban?: string, banks?: Handbook) =>
  iban && validateSaudiIban(normalizeIban(iban)) === null
    ? banks?.find(
        (item) => item.additionalParams?.bankIdentifier === normalizeIban(iban).substring(4, 6)
      )
    : undefined;

export const getEditedBankFields = (
  saved?: KsaReviewBankAccount,
  draft?: KsaReviewBankAccount,
  banks?: Handbook
): Set<string> => {
  const edited = new Set(Object.keys({ ...saved, ...draft }));
  (['agentBankName', 'agentSwiftCode'] as const).forEach((field) => {
    const source = draft && Object.prototype.hasOwnProperty.call(draft, field) ? draft : saved;
    const value = source?.[field];
    const bank = findBank(source?.agentIban ?? saved?.agentIban, banks);
    const derived = field === 'agentBankName' ? bank?.localizedValue : bank?.apiValue;
    const normalized = field === 'agentSwiftCode' ? value?.trim().toUpperCase() : value?.trim();
    // Saved values have no provenance flag; a matching handbook value remains auto-derived.
    if ((field === 'agentSwiftCode' && !normalized) || (derived && normalized === derived)) {
      edited.delete(field);
    }
  });
  return edited;
};

export const getDerivedBankUpdates = (
  iban: string,
  banks: Handbook | undefined,
  editedFields: Set<string>
): KsaReviewBankAccount => {
  const bank = findBank(iban, banks);
  const updates: KsaReviewBankAccount = { derivedBankName: bank?.localizedValue || '' };
  if (!editedFields.has('agentBankName')) updates.agentBankName = bank?.localizedValue || '';
  if (!editedFields.has('agentSwiftCode')) updates.agentSwiftCode = bank?.apiValue || '';
  return updates;
};

export const getInitialBankReview = (
  bankAccount?: KsaBankAccount,
  saved?: KsaReviewBankAccount,
  draft?: KsaReviewBankAccount,
  banks?: Handbook
): KsaReviewBankAccount => {
  const entered = { agentIban: normalizeIban(bankAccount?.iban || ''), ...saved, ...draft };
  const verified = isBankVerified(bankAccount, entered.agentIban);
  const bank = verified ? findBank(bankAccount?.iban, banks) : undefined;
  const beneficiary =
    bankAccount?.accountHolderName?.trim() || bankAccount?.beneficiaryName?.trim() || '';
  const swift = verified ? bankAccount?.swiftCode?.trim() || bank?.apiValue : undefined;
  const review: KsaReviewBankAccount = {
    englishBeneficiaryFullName:
      verified && ENGLISH_BANK_NAME.test(beneficiary) ? beneficiary : '',
    agentBankName:
      verified && ENGLISH_BANK_NAME.test(bankAccount?.bankName?.trim() || '')
        ? bankAccount?.bankName?.trim()
        : '',
    ...(!verified || swift ? { agentSwiftCode: swift || '' } : {}),
    ...entered,
  };
  if (!verified) {
    Object.assign(
      review,
      getDerivedBankUpdates(review.agentIban || '', banks, getEditedBankFields(saved, draft, banks))
    );
  } else {
    review.derivedBankName = bank?.localizedValue || '';
  }
  const nameSource = draft && Object.prototype.hasOwnProperty.call(draft, 'derivedBankName')
    ? draft : saved;
  const sourceIban = nameSource?.agentIban ?? saved?.agentIban ?? bankAccount?.iban;
  const currentIban = verified ? bankAccount?.iban : review.agentIban;
  if (nameSource && Object.prototype.hasOwnProperty.call(nameSource, 'derivedBankName') &&
      normalizeIban(sourceIban || '') === normalizeIban(currentIban || '')) {
    review.derivedBankName = nameSource.derivedBankName;
  }
  return review;
};
